// P2P State Manager for Serverless Reconnection
export class P2PStateManager {
  private static instance: P2PStateManager;
  private gameState: any = null;
  private peers: Map<string, RTCPeerConnection> = new Map();
  private dataChannels: Map<string, RTCDataChannel> = new Map();
  private stateCache = new Map<string, any>();
  private consensusTracker = new Map<string, Map<string, any>>();

  private constructor() {
    this.loadPersistedState();
    this.setupBeforeUnloadHandler();
  }

  static getInstance(): P2PStateManager {
    if (!P2PStateManager.instance) {
      P2PStateManager.instance = new P2PStateManager();
    }
    return P2PStateManager.instance;
  }

  // Persist game state locally for reconnection
  persistGameState(roomId: string, playerId: string, gameState: any): void {
    const stateSnapshot = {
      roomId,
      playerId,
      gameState,
      timestamp: Date.now(),
      stateHash: this.calculateStateHash(gameState),
      peerList: Array.from(this.peers.keys())
    };

    localStorage.setItem('p2p_game_state', JSON.stringify(stateSnapshot));
    this.gameState = gameState;
    this.stateCache.set(roomId, stateSnapshot);
  }

  // Load persisted state for P2P reconnection
  loadPersistedState(): any {
    try {
      const stored = localStorage.getItem('p2p_game_state');
      if (stored) {
        const stateSnapshot = JSON.parse(stored);
        
        // Check if state is recent (within 30 minutes)
        const ageMinutes = (Date.now() - stateSnapshot.timestamp) / (1000 * 60);
        if (ageMinutes < 30) {
          this.gameState = stateSnapshot.gameState;
          return stateSnapshot;
        } else {
          localStorage.removeItem('p2p_game_state');
        }
      }
    } catch (error) {
      console.error('Failed to load persisted state:', error);
      localStorage.removeItem('p2p_game_state');
    }
    return null;
  }

  // Calculate state hash for verification
  private calculateStateHash(state: any): string {
    const stateString = JSON.stringify(state, Object.keys(state).sort());
    return this.simpleHash(stateString);
  }

  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(16);
  }

  // Establish P2P connection with another player
  async connectToPeer(peerId: string, isInitiator: boolean = false): Promise<RTCPeerConnection> {
    const peerConnection = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    });

    this.peers.set(peerId, peerConnection);

    // Create data channel for game state sync
    if (isInitiator) {
      const dataChannel = peerConnection.createDataChannel('gameState', {
        ordered: true
      });
      this.setupDataChannel(peerId, dataChannel);
    }

    // Handle incoming data channels
    peerConnection.ondatachannel = (event) => {
      this.setupDataChannel(peerId, event.channel);
    };

    // Handle connection state changes
    peerConnection.onconnectionstatechange = () => {
      console.log(`P2P connection to ${peerId}: ${peerConnection.connectionState}`);
      
      if (peerConnection.connectionState === 'connected') {
        // Request state sync once connected
        this.requestStateSync(peerId);
      }
    };

    return peerConnection;
  }

  // Setup data channel for P2P communication
  private setupDataChannel(peerId: string, dataChannel: RTCDataChannel): void {
    this.dataChannels.set(peerId, dataChannel);

    dataChannel.onopen = () => {
      console.log(`Data channel with ${peerId} opened`);
    };

    dataChannel.onmessage = (event) => {
      this.handleP2PMessage(peerId, JSON.parse(event.data));
    };

    dataChannel.onerror = (error) => {
      console.error(`Data channel error with ${peerId}:`, error);
    };
  }

  // Handle P2P messages from peers
  private handleP2PMessage(peerId: string, message: any): void {
    switch (message.type) {
      case 'state_sync_request':
        this.handleStateSyncRequest(peerId);
        break;
      
      case 'state_sync_response':
        this.handleStateSyncResponse(peerId, message.data);
        break;
      
      case 'state_update':
        this.handleStateUpdate(peerId, message.data);
        break;
      
      case 'consensus_vote':
        this.handleConsensusVote(peerId, message.data);
        break;
      
      default:
        console.log(`Received P2P message from ${peerId}:`, message);
    }
  }

  // Request state synchronization from a peer
  private requestStateSync(peerId: string): void {
    const dataChannel = this.dataChannels.get(peerId);
    if (dataChannel?.readyState === 'open') {
      dataChannel.send(JSON.stringify({
        type: 'state_sync_request',
        timestamp: Date.now()
      }));
    }
  }

  // Handle state sync request from a peer
  private handleStateSyncRequest(peerId: string): void {
    const dataChannel = this.dataChannels.get(peerId);
    if (dataChannel?.readyState === 'open' && this.gameState) {
      dataChannel.send(JSON.stringify({
        type: 'state_sync_response',
        data: {
          gameState: this.gameState,
          stateHash: this.calculateStateHash(this.gameState),
          timestamp: Date.now()
        }
      }));
    }
  }

  // Handle state sync response from a peer
  private handleStateSyncResponse(peerId: string, data: any): void {
    if (!this.gameState) {
      // No local state, accept peer's state
      this.gameState = data.gameState;
      console.log(`Restored game state from peer ${peerId}`);
      this.notifyStateRestored(data.gameState);
    } else {
      // Validate against local state
      const localHash = this.calculateStateHash(this.gameState);
      if (localHash !== data.stateHash) {
        console.warn(`State mismatch with peer ${peerId}, initiating consensus`);
        this.initiateConsensus();
      }
    }
  }

  // Handle state updates from peers
  private handleStateUpdate(peerId: string, data: any): void {
    // Store update for consensus validation
    if (!this.consensusTracker.has(data.updateId)) {
      this.consensusTracker.set(data.updateId, new Map());
    }
    
    this.consensusTracker.get(data.updateId)!.set(peerId, data);
    
    // Check if we have majority consensus
    this.checkConsensus(data.updateId);
  }

  // Handle consensus votes from peers
  private handleConsensusVote(peerId: string, data: any): void {
    console.log(`Received consensus vote from ${peerId}:`, data);
    // Implementation for consensus voting mechanism
  }

  // Initiate consensus protocol when states don't match
  private initiateConsensus(): void {
    const consensusId = `consensus_${Date.now()}`;
    const message = {
      type: 'consensus_vote',
      data: {
        consensusId,
        proposedState: this.gameState,
        stateHash: this.calculateStateHash(this.gameState)
      }
    };

    // Send to all connected peers
    this.dataChannels.forEach((channel, peerId) => {
      if (channel.readyState === 'open') {
        channel.send(JSON.stringify(message));
      }
    });
  }

  // Check if consensus has been reached for an update
  private checkConsensus(updateId: string): void {
    const votes = this.consensusTracker.get(updateId);
    if (!votes) return;

    const totalPeers = this.dataChannels.size;
    const requiredVotes = Math.floor(totalPeers / 2) + 1;

    if (votes.size >= requiredVotes) {
      // Count votes by state hash
      const hashCounts = new Map<string, number>();
      let winningUpdate: any = null;
      let maxVotes = 0;

      votes.forEach((update, peerId) => {
        const hash = this.calculateStateHash(update.gameState);
        const count = (hashCounts.get(hash) || 0) + 1;
        hashCounts.set(hash, count);

        if (count > maxVotes) {
          maxVotes = count;
          winningUpdate = update;
        }
      });

      if (maxVotes >= requiredVotes) {
        // Apply consensus state
        this.gameState = winningUpdate.gameState;
        this.notifyStateUpdated(this.gameState);
        this.consensusTracker.delete(updateId);
      }
    }
  }

  // Attempt P2P reconnection without server data exchange
  async reconnectP2P(roomId: string, playerId: string): Promise<boolean> {
    try {
      console.log('Attempting P2P reconnection...');
      
      // 1. Load persisted state
      const persistedState = this.loadPersistedState();
      if (!persistedState || persistedState.roomId !== roomId) {
        throw new Error('No valid persisted state found');
      }

      // 2. Try to reconnect to known peers
      const reconnectionPromises = persistedState.peerList.map(async (peerId: string) => {
        if (peerId !== playerId) {
          try {
            const peerConnection = await this.connectToPeer(peerId, true);
            // Create offer for reconnection
            const offer = await peerConnection.createOffer();
            await peerConnection.setLocalDescription(offer);
            
            // In a full implementation, you'd need signaling
            // For now, we'll simulate successful reconnection
            return true;
          } catch (error) {
            console.error(`Failed to reconnect to peer ${peerId}:`, error);
            return false;
          }
        }
        return false;
      });

      const results = await Promise.all(reconnectionPromises);
      const successfulReconnections = results.filter(Boolean).length;

      if (successfulReconnections > 0) {
        console.log(`Successfully reconnected to ${successfulReconnections} peers`);
        
        // Wait for state synchronization
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        return true;
      } else {
        throw new Error('Failed to reconnect to any peers');
      }
    } catch (error) {
      console.error('P2P reconnection failed:', error);
      return false;
    }
  }

  // Broadcast state update to all peers
  broadcastStateUpdate(updateId: string, gameState: any): void {
    const message = {
      type: 'state_update',
      data: {
        updateId,
        gameState,
        stateHash: this.calculateStateHash(gameState),
        timestamp: Date.now()
      }
    };

    this.dataChannels.forEach((channel, peerId) => {
      if (channel.readyState === 'open') {
        channel.send(JSON.stringify(message));
      }
    });

    // Update local state
    this.gameState = gameState;
  }

  // Get current P2P connection status
  getP2PStatus(): any {
    const connectedPeers = Array.from(this.peers.entries())
      .filter(([_, pc]) => pc.connectionState === 'connected')
      .map(([peerId, _]) => peerId);

    return {
      totalPeers: this.peers.size,
      connectedPeers: connectedPeers.length,
      peerIds: connectedPeers,
      hasGameState: !!this.gameState,
      stateHash: this.gameState ? this.calculateStateHash(this.gameState) : null
    };
  }

  // Clean up P2P connections
  disconnect(): void {
    this.dataChannels.forEach(channel => {
      if (channel.readyState === 'open') {
        channel.close();
      }
    });

    this.peers.forEach(pc => {
      pc.close();
    });

    this.dataChannels.clear();
    this.peers.clear();
    this.consensusTracker.clear();
  }

  // Setup handler to persist state before page unload
  private setupBeforeUnloadHandler(): void {
    window.addEventListener('beforeunload', () => {
      if (this.gameState) {
        // State is already persisted via persistGameState
        console.log('Game state preserved for P2P reconnection');
      }
    });
  }

  // Callback handlers (to be set by the main app)
  private onStateRestored?: (gameState: any) => void;
  private onStateUpdated?: (gameState: any) => void;

  setStateRestoredHandler(handler: (gameState: any) => void): void {
    this.onStateRestored = handler;
  }

  setStateUpdatedHandler(handler: (gameState: any) => void): void {
    this.onStateUpdated = handler;
  }

  private notifyStateRestored(gameState: any): void {
    if (this.onStateRestored) {
      this.onStateRestored(gameState);
    }
  }

  private notifyStateUpdated(gameState: any): void {
    if (this.onStateUpdated) {
      this.onStateUpdated(gameState);
    }
  }
}